import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { GuiaCnesGenerico, GuiaCnesIncompativel } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, takeUntil } from 'rxjs/operators';
import { CnesGenericoIncompativelService } from './cnes-generico-incompativel.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.CNES_GENERICO_INCOMPATIVEL';
const PAGE_TOOLTIP = 'RELATORIOS_PERSONALIZADOS.CNES_GENERICO_INCOMPATIVEL';

const TAB_GENERICO = 0;
const TAB_INCOMPATIVEL = 1;

const COLUMNS_GENERICO: ColDef[] = [
  {
    headerName: 'CPF/CNPJ',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
    width: 180,
  },
  {
    headerName: 'Executante',
    field: 'nm_empresarial',
    width: 360,
  },
  {
    headerName: 'CNES Correto',
    field: 'cnes_correto',
    width: 140,
  },
  {
    headerName: 'Qtde. Guia',
    field: 'qt_guia',
    type: 'int',
    width: 120,
  },
  {
    headerName: 'Tp Rede',
    field: 'nm_tp_rede',
    width: 160,
  },
];

const COLUMNS_INCOMPATIVEL: ColDef[] = [
  {
    headerName: 'CNPJ executante da guia',
    field: 'nr_cnpj_cpf_guia',
    type: 'cpfCnpj',
    width: 180,
  },
  {
    headerName: 'Nome do CNPJ executante no DATASUS',
    field: 'ds_executante',
    width: 360,
  },
  {
    headerName: 'CNES incompatível da guia',
    field: 'nr_cnes_guia',
    width: 160,
  },
  {
    headerName: 'CNES compatível com o CNPJ exec.',
    field: 'nr_cnes_datasus',
    width: 220,
  },
  {
    headerName: 'Qtde. Guias',
    field: 'qt_guia',
    type: 'int',
    width: 120,
  },
  {
    headerName: 'Tp Rede',
    field: 'nm_tp_rede',
    width: 160,
  },
];

/**
 * Relatório de guias criticadas por CNES do executante, em duas abas com o mesmo intervalo de competências:
 * - CNES genérico (crítica 7435): executantes que informaram o CNES genérico, mas têm CNES ativo no DATASUS;
 * - CNES incompatível (crítica 9364): pares executante × CNES informado que divergem do cadastro do DATASUS.
 */
@Component({
  templateUrl: './cnes-generico-incompativel.component.html',
  styleUrls: ['./cnes-generico-incompativel.component.scss'],
  providers: [CnesGenericoIncompativelService, DecimalPipe, DatePipe],
  standalone: false,
})
export class CnesGenericoIncompativelComponent implements OnInit, OnDestroy {
  readonly TAB_GENERICO = TAB_GENERICO;
  readonly TAB_INCOMPATIVEL = TAB_INCOMPATIVEL;
  readonly GRID_GENERICO_KEY = GRID_KEY + '.GENERICO';
  readonly GRID_INCOMPATIVEL_KEY = GRID_KEY + '.INCOMPATIVEL';

  @ViewChild('agGridGenerico') agGridGenerico: AgGridAngular<RowGenerico>;
  @ViewChild('agGridIncompativel') agGridIncompativel: AgGridAngular<RowIncompativel>;

  gridOptionsGenerico: GridOptions<RowGenerico>;
  gridOptionsIncompativel: GridOptions<RowIncompativel>;

  currentGrid: AgGridAngular;
  currentGridKey: string;

  tabIndex = TAB_GENERICO;

  pageReady$ = new Subject<void>();

  firstLoadGenerico = true;
  firstLoadIncompativel = true;

  filterChangedGenerico = true;
  filterChangedIncompativel = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  rowDataGenerico: RowGenerico[];
  rowDataIncompativel: RowIncompativel[];

  dtUltimoProcessamento: string = null;

  pageTooltip = PAGE_TOOLTIP;

  fullscreen$: Observable<boolean>;

  constructor(
    private permissaoService: PermissaoService,
    private service: CnesGenericoIncompativelService,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        distinctUntilChanged((anterior, atual) => anterior.cd_registro_ans === atual.cd_registro_ans),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        const trocouOperadora = !!this.operadora;

        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        if (trocouOperadora) {
          this.limparAbas();
          return;
        }

        setTimeout(() => {
          this.selecionarGridDaAba();
          this.pageReady$.next();
        });
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params]) => this.applyQueryFilters(params));

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    const pipes = {
      decimal: this.decimalPipe,
      cpfCnpj: this.cpfCnpjPipe,
    };

    this.gridOptionsGenerico = buildGridOptions<RowGenerico>({
      columnDefs: COLUMNS_GENERICO,
      pipes,
      views: {
        gridKey: this.GRID_GENERICO_KEY,
        hide: !this.podeGerenciarVisoes,
      },
      exportExcelFn: () => this.exportExcel(),
    });

    this.gridOptionsIncompativel = buildGridOptions<RowIncompativel>({
      columnDefs: COLUMNS_INCOMPATIVEL,
      pipes,
      views: {
        gridKey: this.GRID_INCOMPATIVEL_KEY,
        hide: !this.podeGerenciarVisoes,
      },
      exportExcelFn: () => this.exportExcel(),
    });
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, tab, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (+tab === TAB_INCOMPATIVEL) {
      this.tabIndex = TAB_INCOMPATIVEL;
      this.selecionarGridDaAba();
    }

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() =>
      this.gridService.applyVisaoInicial(this.currentGridKey, this.currentGrid, visao, sort, filterModel),
    );

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    if (hasFilter) {
      this.reloadData();
    }
  }

  reloadData() {
    this.carregarUltimoProcessamento();

    if (this.tabIndex === TAB_INCOMPATIVEL) {
      this.carregarIncompativel();
    } else {
      this.carregarGenerico();
    }
  }

  private carregarGenerico() {
    this.filterChangedGenerico = false;
    this.mostrarCarregando(this.agGridGenerico, this.firstLoadGenerico);
    this.firstLoadGenerico = false;

    const [competenciaInicial, competenciaFinal] = this.competenciaRange;

    if (!competenciaInicial || !competenciaFinal) {
      this.rowDataGenerico = [];
      this.agGridGenerico?.api?.setGridOption('loading', false);
      return;
    }

    this.service
      .getGuiasCnesGenerico(this.operadora, competenciaInicial, competenciaFinal)
      .pipe(takeUntil(this.cancelarCargas$))
      .subscribe({
        next: items => {
          this.rowDataGenerico = items.map(item => ({
            ...item,
            nm_tp_rede: this.geralService.buildNomeDeDominio(item.tp_rede, 'MON_TIPO_REDE'),
          }));
        },
        error: error => {
          this.notificationService.handleError(error);
          this.agGridGenerico?.api?.setGridOption('loading', false);
        },
        complete: () => this.agGridGenerico?.api?.setGridOption('loading', false),
      });
  }

  private carregarIncompativel() {
    this.filterChangedIncompativel = false;
    this.mostrarCarregando(this.agGridIncompativel, this.firstLoadIncompativel);
    this.firstLoadIncompativel = false;

    const [competenciaInicial, competenciaFinal] = this.competenciaRange;

    if (!competenciaInicial || !competenciaFinal) {
      this.rowDataIncompativel = [];
      this.agGridIncompativel?.api?.setGridOption('loading', false);
      return;
    }

    this.service
      .getGuiasCnesIncompativel(this.operadora, competenciaInicial, competenciaFinal)
      .pipe(takeUntil(this.cancelarCargas$))
      .subscribe({
        next: items => {
          this.rowDataIncompativel = items.map(item => ({
            ...item,
            nm_tp_rede: this.geralService.buildNomeDeDominio(item.tp_rede, 'MON_TIPO_REDE'),
          }));
        },
        error: error => {
          this.notificationService.handleError(error);
          this.agGridIncompativel?.api?.setGridOption('loading', false);
        },
        complete: () => this.agGridIncompativel?.api?.setGridOption('loading', false),
      });
  }

  private carregarUltimoProcessamento() {
    this.service
      .getUltimoProcessamento(this.operadora)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.cancelarCargas$),
      )
      .subscribe(dt => (this.dtUltimoProcessamento = dt ? this.datePipe.transform(Number(dt), 'dd/MM/yyyy') : null));
  }

  private mostrarCarregando(grid: AgGridAngular, primeiraCarga: boolean) {
    if (primeiraCarga) {
      grid?.api?.setGridOption('loading', false);
      setTimeout(() => grid?.api?.setGridOption('loading', true));
    } else {
      grid?.api?.setGridOption('loading', true);
    }
  }

  private limparAbas() {
    this.cancelarCargas$.next();

    this.rowDataGenerico = [];
    this.rowDataIncompativel = [];
    this.dtUltimoProcessamento = null;
    this.firstLoadGenerico = true;
    this.firstLoadIncompativel = true;
    this.filterChangedGenerico = true;
    this.filterChangedIncompativel = true;

    setTimeout(() => {
      this.agGridGenerico?.api?.setGridOption('loading', true);
      this.agGridIncompativel?.api?.setGridOption('loading', true);
    });
  }

  onSearch(value: string) {
    this.agGridGenerico?.api?.updateGridOptions({ quickFilterText: value });
    this.agGridIncompativel?.api?.updateGridOptions({ quickFilterText: value });
  }

  onTabChange() {
    this.selecionarGridDaAba();
    setTimeout(() => this.gridService.applyVisaoInicial(this.currentGridKey, this.currentGrid));
  }

  private selecionarGridDaAba() {
    if (this.tabIndex === TAB_INCOMPATIVEL) {
      this.currentGrid = this.agGridIncompativel;
      this.currentGridKey = this.GRID_INCOMPATIVEL_KEY;
    } else {
      this.currentGrid = this.agGridGenerico;
      this.currentGridKey = this.GRID_GENERICO_KEY;
    }
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChangedGenerico = true;
    this.filterChangedIncompativel = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const [titulo, sufixoArquivo] =
      this.tabIndex === TAB_INCOMPATIVEL
        ? ['Guias com CNES incompatível com o DATASUS', 'incompativel']
        : ['Guias com CNES genérico', 'generico'];

    const fileNamePrefix = `cnes-${sufixoArquivo}-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      titulo,
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: this.currentGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(this.currentGridKey);
    const sort = this.gridService.getSimpleSort(this.currentGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.currentGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      tab: this.tabIndex,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private cancelarCargas$ = new Subject<void>();
  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.cancelarCargas$.complete();
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface RowGenerico extends GuiaCnesGenerico {
  nm_tp_rede: string;
}

interface RowIncompativel extends GuiaCnesIncompativel {
  nm_tp_rede: string;
}
